import React, { useEffect, useRef, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import useSignOut from "../../utils/sign-out";
import { useTranslation } from "react-i18next";

const AppPopOver = ({
  isPopoverOpen,
  setIsPopoverOpen,
  image,
  label,
  content,
  subContent,
  classNameList,
  className,
  type,
  accountNo,
  privacyLabel,
  forceDownward = false,
  enableDynamicPositioning = false,
  triggerRef,
}) => {
  const popoverRef = useRef(null);
  const signOut = useSignOut();
  const { t } = useTranslation();
  const [positionStyle, setPositionStyle] = useState({});
  const [isPositioned, setIsPositioned] = useState(false);
  const handleClickOutside = useCallback(
    (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsPopoverOpen(false);
      }
    },
    [setIsPopoverOpen]
  );

  const handleScroll = useCallback(() => {
    setIsPopoverOpen(false);
  }, [setIsPopoverOpen]);

  useEffect(() => {
    if (isPopoverOpen) {
      setIsPositioned(false); // Reset visibility when opening
    }
  }, [isPopoverOpen]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    if (!enableDynamicPositioning || !isPopoverOpen || !popoverRef.current)
      return;

    const rect = popoverRef.current.getBoundingClientRect();

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const spaceRight = window.innerWidth - rect.right;

    const newPosition = {};

    // Force downward if flagged
    if (forceDownward) {
      newPosition.top = "100%";
      newPosition.bottom = "auto";
      newPosition.marginTop = "0.5rem";
      newPosition.marginBottom = "0";
    } else {
      if (spaceBelow < 150 && spaceAbove > 150) {
        newPosition.top = "auto";
        newPosition.bottom = "100%";
        newPosition.marginTop = "0";
        newPosition.marginBottom = "0.5rem";
      } else {
        newPosition.top = "100%";
        newPosition.bottom = "auto";
        newPosition.marginTop = "0.5rem";
        newPosition.marginBottom = "0";
      }
    }

    // Horizontal
    newPosition.right = spaceRight < 200 ? "0.5rem" : "0";
    newPosition.left = "auto";
    newPosition.position = "absolute";
    newPosition.zIndex = 10;

    setPositionStyle(newPosition);
    setIsPositioned(true);
  }, [isPopoverOpen, forceDownward, enableDynamicPositioning]);

  return (
    <>
      {isPopoverOpen && (
        <div
          ref={popoverRef}
          className={`absolute rounded-lg shadow dark:bg-gray-700  ${className} ${
            type !== "Header" ? "w-48 " : "lg:right-10 z-10 right-2"
          }`}
          style={positionStyle}
        >
          {image && (
            <>
              <div className="py-2 text-center text-[12px] flex flex-col items-center max-w-full text-wrap">
                <div
                  className="rounded-full cursor-pointer bg-insightful-light-purple p-1 overflow-hidden"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPopoverOpen(false);
                  }}
                >
                  <img
                    src={image}
                    alt="Avatar"
                    className="object-cover rounded-full transition-transform transform hover:scale-110 w-10 h-10"
                  />
                </div>
                <p className="text-insightful-black dark:text-gray-200 font-bold text-sm px-4 whitespace-normal break-all">
                  {label}
                </p>
                <p className="text-insightful-black dark:text-gray-200 font-bold text-[10px] truncate px-4">
                  {t("header.account_number")}: {accountNo}
                </p>
              </div>
              <div className="border-t border-gray-100 dark:border-gray-100" />
            </>
          )}

          <ul
            className={`py-3 text-insightful-black dark:text-gray-200 list-outside ${classNameList}`}
          >
            {content?.map((item, index) => (
              <li key={index}>
                <div
                  onClick={(e) => item.onClick?.(e)}
                  className={`flex items-start cursor-pointer px-4 py-2 hover:bg-insightful-lightest-gray dark:hover:bg-gray-600 dark:hover:text-white ${
                    type === "Header" ? "justify-center text-center" : ""
                  }`}
                >
                  {item.icon && (
                    <img
                      src={item.icon}
                      className="w-4 h-4 mt-1 mr-2"
                      alt={item.label}
                    />
                  )}
                  <span className="leading-tight">{item.label}</span>
                </div>
              </li>
            ))}
          </ul>

          {subContent && (
            <div className="border-t border-gray-100 dark:border-gray-600" />
          )}

          <ul
            className={`text-sm text-insightful-black dark:text-gray-200 ${classNameList}`}
          >
            {subContent?.map((item, index) => (
              <li key={index}>
                <div
                  onClick={(e) => item.onClick?.(e)}
                  className={`block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white cursor-pointer ${
                    item.isDestructive ? "text-red-800" : ""
                  }`}
                >
                  {item.icon && (
                    <img
                      src={item.icon}
                      className="w-4 h-4 mr-2 inline-block"
                      alt={item.label}
                    />
                  )}
                  {item.label}
                </div>
              </li>
            ))}
          </ul>

          {type === "Header" && (
            <div className="flex flex-col gap-2">
              <div className="border-t border-gray-100 dark:border-gray-600" />
              <div className="text-center">
                <button
                  className="flex justify-center items-center h-[40px] w-[80%] border border-transparent rounded-full shadow-sm text-[16px] font-bold text-[#444C5E] bg-[#D9D9D9] hover:bg-insightful-lightest-gray focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 mb-4 mx-auto"
                  onClick={() => signOut({ isNavigate: true })}
                >
                  {t("header.signout")}
                </button>
              </div>
            </div>
          )}

          {privacyLabel && (
            <div className="border-t border-gray-100 dark:border-gray-600" />
          )}

          <div className="flex flex-row w-full justify-center items-center gap-2">
            {privacyLabel?.map((item, index) => (
              <React.Fragment key={index}>
                <Link
                  className="text-[12px] text-insightful-black dark:text-gray-200 py-2 cursor-pointer"
                  to={item.path}
                  target="_blank"
                  onClick={() => setIsPopoverOpen(false)}
                >
                  {item.label}
                </Link>
                {index < privacyLabel.length - 1 && (
                  <span className="text-[12px] text-insightful-black dark:text-gray-200 py-2">
                    |
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default AppPopOver;
